import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  SUBSCRIPTION_END_REFUND_QUEUE_LABEL,
  type SubscriptionEndRefundList,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { AdminCloseWorkItem } from "@/components/AdminCloseWorkItem";
import { UnauthorizedError } from "@/lib/clinician-api";
import { adminPatientHref } from "@/lib/patient-links";
import { endRefundViewRow } from "@/lib/subscription-end-refunds";
import { fetchSubscriptionEndRefunds } from "@/lib/subscription-end-refunds-api";

// TES-1554 (Ö-860–Ö-863) — "Avslut och återbetalning". When the doctor ends
// the drug treatment because the patient is no longer suitable for it, the
// subscription ends from that date and the paid days after it are refunded:
// "Manuell hantering av admin i första versionen … Admin verkställer avslut
// och återbetalning."
//
// Admin only and read-only towards the patient (TES-1040): the end and the
// refund are made in the Stripe dashboard, and the task is closed here with
// "Stäng arbetsmoment" and a note of what was done. The list names people,
// so the API logs it as a read of each patient it returns.
export function SubscriptionEndRefundsRoute() {
  const { signOut, claims } = useAuth();
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;
  const [list, setList] = useState<SubscriptionEndRefundList | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setList(await fetchSubscriptionEndRefunds());
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOutRef.current();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="flex flex-col gap-md">
      <div className="flex flex-col gap-xs">
        <h1 className="font-display text-3xl tracking-tight">
          {SUBSCRIPTION_END_REFUND_QUEUE_LABEL}
        </h1>
        <p className="text-sm text-textSecondary max-w-2xl">
          Läkaren har avslutat läkemedelsbehandlingen för att patienten inte
          längre bedöms lämplig för den. Bindnings- och uppsägningstiden gäller
          inte från avslutsdatumet. Avsluta abonnemanget och återbetala beloppet
          i Stripe, och stäng sedan arbetsmomentet med vad du gjorde.
          Återbetalningen räknas per dag från avslutsdatumet, inklusive den
          dagen, till den betalda periodens slut, och bara på det som faktiskt
          betalats. Internt: listan loggas som åtkomst till varje person den
          visar.
        </p>
      </div>
      {list && !list.enabled ? (
        <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary text-sm">
          Funktionen är avstängd. Läkarens beslut skapar inga nya uppgifter
          förrän den slås på.
        </div>
      ) : null}
      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
          {error}
        </div>
      ) : null}
      {list === null && !error ? (
        <p className="text-sm text-textSecondary">Hämtar…</p>
      ) : null}
      {list ? (
        <SubscriptionEndRefundsView
          rows={list.rows}
          actingRole={claims?.role}
          onClosed={() => void load()}
          onUnauthorized={() => void signOutRef.current()}
        />
      ) : null}
    </div>
  );
}

/** The table itself, or the empty state. */
export function SubscriptionEndRefundsView({
  rows,
  actingRole,
  onClosed,
  onUnauthorized,
}: {
  rows: SubscriptionEndRefundList["rows"];
  actingRole: string | undefined;
  onClosed: () => void;
  onUnauthorized?: () => void;
}) {
  if (rows.length === 0) {
    return (
      <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary text-sm">
        Inga avslut att hantera.
      </div>
    );
  }
  return (
    <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
            <th className="px-md py-sm font-medium">Patient</th>
            <th className="px-md py-sm font-medium">Avslutsdatum</th>
            <th className="px-md py-sm font-medium">Abonnemang</th>
            <th className="px-md py-sm font-medium">Återbetala</th>
            <th className="px-md py-sm font-medium" />
          </tr>
        </thead>
        <tbody>
          {rows.map(endRefundViewRow).map((r) => (
            <tr
              key={r.taskId}
              className={`border-t border-surfaceMuted align-top ${
                r.open ? "" : "text-textSecondary"
              }`}
            >
              <td className="px-md py-sm font-medium">{r.name}</td>
              <td className="px-md py-sm">{r.endDate}</td>
              <td className="px-md py-sm">
                {r.subscription}
                <span className="block text-xs text-textSecondary">
                  {r.stripeSubscriptionId}
                </span>
              </td>
              <td className="px-md py-sm">
                <span className="font-semibold">{r.refund}</span>
                <ul className="mt-xs flex flex-col gap-xs text-xs text-textSecondary">
                  {r.working.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
                {r.newCharge ? (
                  <p className="mt-xs text-xs text-danger">{r.newCharge}</p>
                ) : null}
              </td>
              <td className="px-md py-sm">
                <div className="flex flex-col items-start gap-sm">
                  <Link
                    to={adminPatientHref(actingRole, r.patientId)}
                    className="text-brand hover:underline"
                  >
                    Patienten
                  </Link>
                  {r.open ? (
                    <AdminCloseWorkItem
                      kind="subscription_end_refund"
                      itemId={r.taskId}
                      onClosed={onClosed}
                      onUnauthorized={onUnauthorized}
                    />
                  ) : (
                    <span className="text-xs">Hanterad {r.closedAt}</span>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
